import React, { useState, useEffect, useRef } from 'react';
import {
  Speech,
  HandMetal,
  SquareMinus,
  ListOrdered,
  RotateCcw,
  RemoveFormatting,
  Underline as UnderlineIcon,
  Type,
  Languages,
  File,
  Settings,
  ChevronDown,
  Check,
} from 'lucide-react';
import SymbolsMenu from './SymbolsMenu';
import GuideWidthDialog from './GuideWidthDialog';
import MarginWidthDialog from './MarginWidthDialog';
import LineStylesDialog from './LineStylesDialog';
import AboutDialog from './AboutDialog';
import WelcomeTour, { shouldAutoShowTour, markTourSeen } from './WelcomeTour';
import { formatShortcut } from '../../utils/shortcuts';

// Standard transcription symbols (for transcribing talk)
const speechSymbols = [
  { symbol: '.', description: 'final fall' },
  { symbol: '?', description: 'final rise' },
  { symbol: ',', description: 'final slight rise' },
  { symbol: '_', description: 'final level' },
  { symbol: '¿', description: 'final low rise' },
  { symbol: ':', description: 'prolongation' },
  { symbol: '-', description: 'cut-off' },
  { symbol: '[', description: 'overlap onset' },
  { symbol: ']', description: 'overlap offset' },
  { symbol: '()', description: 'unclear/silence' },
  { symbol: '(())', description: 'description' },
  { symbol: '=', description: 'no break' },
  { symbol: '<', description: 'left push' },
  { symbol: '><', description: 'fast' },
  { symbol: '<>', description: 'slow' },
  { symbol: '►', description: 'abrupt-join' },
  { symbol: '°', description: 'quiet' },
  { symbol: '~', description: 'tremolo' },
  { symbol: '#', description: 'creak' },
  { symbol: '£', description: 'smile voice' },
  { symbol: '↑', description: 'high pitch' },
  { symbol: '↓', description: 'low pitch' },
];

// Multimodal symbols (for indicating embodied actions — no labels needed)
const embodiedSymbols = [
  { symbol: '*' },
  { symbol: '•' },
  { symbol: '●' },
  { symbol: '○' },
  { symbol: '¤' },
  { symbol: '☼' },
  { symbol: '♦' },
  { symbol: '◊' },
  { symbol: '■' },
  { symbol: '◘' },
  { symbol: '□' },
  { symbol: '+' },
  { symbol: '†' },
  { symbol: '‡' },
  { symbol: '±' },
  { symbol: '÷' },
  { symbol: '×' },
  { symbol: '∞' },
  { symbol: '≈' },
  { symbol: '≠' },
  { symbol: '≡' },
  { symbol: '§' },
  { symbol: '¶' },
  { symbol: '▲' },
  { symbol: '▼' },
  { symbol: '¢' },
  { symbol: '€' },
  { symbol: '¥' },
  { symbol: '₪' },
  { symbol: '©' },
  { symbol: '®' },
];

const Toolbar = ({
  editor,
  onMarkSpeech,
  onMarkEmbodied,
  onMarkGloss,
  onMarkTranslation,
  onMarkDefault,
  onNumberLines,
  onResetFormatting,
  onClearFormatting,
  onInsertSymbol,
  currentLineType,
  lineNumbersEnabled,
  mode,
  multilingual,
  fileActions,
  optionsActions,
}) => {
  const isAudible = mode === 'audible';
  const [openMenu, setOpenMenu] = useState(null);
  const [guideDialogOpen, setGuideDialogOpen] = useState(false);
  const [marginDialogOpen, setMarginDialogOpen] = useState(false);
  const [lineStylesDialogOpen, setLineStylesDialogOpen] = useState(false);
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  // Shown automatically on a browser's first visit, and from Options after
  const [tourOpen, setTourOpen] = useState(shouldAutoShowTour);
  const menuAreaRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (menuAreaRef.current && !menuAreaRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Mark the tour seen as soon as it opens, so it never reappears on its own
  // (even if the tab is closed mid-tour).
  useEffect(() => {
    if (tourOpen) markTourSeen();
  }, [tourOpen]);

  const handleMenuAction = (action) => {
    setOpenMenu(null);
    action();
  };

  if (!editor) {
    return null;
  }

  const menus = {
    file: {
      label: 'File',
      icon: File,
      items: [
        { label: 'New', shortcut: formatShortcut(['Mod'], 'N'), action: fileActions.onNew },
        { label: 'Open\u2026', shortcut: formatShortcut(['Mod'], 'O'), action: fileActions.onOpen },
        { type: 'separator' },
        { label: 'Save', shortcut: formatShortcut(['Mod'], 'S'), action: fileActions.onSave },
        { label: 'Save As\u2026', shortcut: formatShortcut(['Mod', 'Shift'], 'S'), action: fileActions.onSaveAs },
        { type: 'separator' },
        { label: 'Export as Text\u2026', action: fileActions.onExportText },
        { label: 'Export as Word\u2026', action: fileActions.onExportWord },
      ],
    },
    options: {
      label: 'Options',
      icon: Settings,
      items: [
        { type: 'toggle', label: 'Multimodal transcription', checked: !isAudible, action: () => optionsActions.onSetMode('multimodal') },
        { type: 'toggle', label: 'Audible transcription', checked: isAudible, action: () => optionsActions.onSetMode('audible') },
        { type: 'separator' },
        { type: 'toggle', label: 'Multilingual transcript', checked: !!optionsActions.multilingual, action: optionsActions.onToggleMultilingual },
        { type: 'separator' },
        { label: 'Zoom In', shortcut: formatShortcut(['Mod'], '+'), action: optionsActions.onZoomIn },
        { label: 'Zoom Out', shortcut: formatShortcut(['Mod'], '\u2212'), action: optionsActions.onZoomOut },
        { label: 'Reset Zoom', shortcut: formatShortcut(['Mod'], '0'), action: optionsActions.onResetZoom },
        { type: 'separator' },
        { type: 'toggle', label: 'Dark Mode', checked: optionsActions.darkMode, action: optionsActions.onToggleDarkMode },
        { type: 'separator' },
        { type: 'toggle', label: 'Spellcheck', checked: optionsActions.spellcheckEnabled, action: optionsActions.onToggleSpellcheck },
        { type: 'separator' },
        { type: 'toggle', label: 'Guide', checked: optionsActions.guideEnabled, action: optionsActions.onToggleGuide },
        { label: 'Guide width\u2026', action: () => setGuideDialogOpen(true) },
        { type: 'separator' },
        { label: 'Margin width\u2026', action: () => setMarginDialogOpen(true) },
        { type: 'separator' },
        { label: 'Line styles\u2026', action: () => setLineStylesDialogOpen(true) },
        { type: 'separator' },
        { label: 'Welcome tour\u2026', action: () => setTourOpen(true) },
        { label: 'About Ript\u2026', action: () => setAboutDialogOpen(true) },
      ],
    },
  };

  return (
    <div className="toolbar">
      {/* LEFT: formatting tools */}
      <div className="toolbar-group">
        {isAudible ? (
          /* Audible mode: a single binary toggle. A line is either a
             numbered transcript line (speech) or an unnumbered line. */
          <button
            className={`toolbar-button ${currentLineType === 'speech' ? 'active' : ''}`}
            onClick={onMarkSpeech}
            aria-pressed={currentLineType === 'speech'}
            title={`Mark current line as a numbered transcript line (${formatShortcut(['Alt'], '1')})`}
            data-tour="line-type"
          >
            <Speech size={16} /> <span className="toolbar-label">Transcript line</span>
          </button>
        ) : (
          <>
            <button
              className={`toolbar-button ${currentLineType === 'speech' ? 'active' : ''}`}
              onClick={onMarkSpeech}
              aria-pressed={currentLineType === 'speech'}
              title={`Mark current line as speech (${formatShortcut(['Alt'], '1')})`}
              data-tour="line-type"
            >
              <Speech size={16} /> <span className="toolbar-label">Speech</span>
            </button>

            <button
              className={`toolbar-button ${currentLineType === 'embodied' ? 'active' : ''}`}
              onClick={onMarkEmbodied}
              aria-pressed={currentLineType === 'embodied'}
              title={`Mark current line as embodied action (${formatShortcut(['Alt'], '2')})`}
            >
              <HandMetal size={16} /> <span className="toolbar-label">Embodied</span>
            </button>

            <button
              className={`toolbar-button ${currentLineType === 'default' ? 'active' : ''}`}
              onClick={onMarkDefault}
              aria-pressed={currentLineType === 'default'}
              title={`Mark current line as default (${formatShortcut(['Alt'], '3')})`}
            >
              <SquareMinus size={16} /> <span className="toolbar-label">Default</span>
            </button>
          </>
        )}

        {multilingual && (
          <>
            <button
              className={`toolbar-button ${currentLineType === 'gloss' ? 'active' : ''}`}
              onClick={onMarkGloss}
              aria-pressed={currentLineType === 'gloss'}
              title={`Mark current line as a gloss (${formatShortcut(['Alt'], '4')})`}
            >
              <Type size={16} /> <span className="toolbar-label">Gloss</span>
            </button>

            <button
              className={`toolbar-button ${currentLineType === 'translation' ? 'active' : ''}`}
              onClick={onMarkTranslation}
              aria-pressed={currentLineType === 'translation'}
              title={`Mark current line as a translation (${formatShortcut(['Alt'], '5')})`}
            >
              <Languages size={16} /> <span className="toolbar-label">Translation</span>
            </button>
          </>
        )}

        <div className="separator"></div>

        <button
          className={`toolbar-button ${lineNumbersEnabled ? 'active' : ''}`}
          onClick={onNumberLines}
          title="Toggle line numbers for speech lines"
          data-tour="line-numbers"
        >
          <ListOrdered size={16} />
        </button>

        <button
          className="toolbar-button"
          onClick={onResetFormatting}
          title="Remove all line classifications and numbering"
        >
          <RotateCcw size={16} />
        </button>

        <button
          className="toolbar-button"
          onClick={onClearFormatting}
          title="Clear all inline formatting and line classifications (preserves underline and line numbers)"
        >
          <RemoveFormatting size={16} />
        </button>

        <div className="separator"></div>

        <button
          className={`toolbar-button ${editor.isActive('underline') ? 'active' : ''}`}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title={`Underline (${formatShortcut(['Mod'], 'U')})`}
        >
          <UnderlineIcon size={16} />
        </button>

        <div className="separator"></div>

        <SymbolsMenu
          title="Speech"
          symbols={speechSymbols}
          onInsertSymbol={onInsertSymbol}
          tourTarget="symbols"
        />

        {!isAudible && (
          <SymbolsMenu
            title="Embodied"
            symbols={embodiedSymbols}
            onInsertSymbol={onInsertSymbol}
          />
        )}
      </div>

      {/* SPACER */}
      <div className="toolbar-spacer"></div>

      {/* RIGHT: File and Options menus */}
      <div className="toolbar-group" ref={menuAreaRef}>
        {Object.entries(menus).map(([key, menu]) => (
          <div key={key} className="menu-container" data-tour={`menu-${key}`}>
            <button
              className={`toolbar-button ${openMenu === key ? 'active' : ''}`}
              onClick={() => setOpenMenu(openMenu === key ? null : key)}
              onMouseEnter={() => openMenu && setOpenMenu(key)}
            >
              <menu.icon size={16} /> <ChevronDown size={14} />
            </button>
            {openMenu === key && (
              <div className="menu-dropdown">
                {menu.items.map((item, i) =>
                  item.type === 'separator' ? (
                    <div key={i} className="menu-separator" />
                  ) : item.type === 'toggle' ? (
                    <button
                      key={i}
                      className="menu-item"
                      onClick={() => handleMenuAction(item.action)}
                    >
                      <span>{item.label}</span>
                      <span className="menu-check">{item.checked ? <Check size={14} /> : null}</span>
                    </button>
                  ) : (
                    <button
                      key={i}
                      className="menu-item"
                      onClick={() => handleMenuAction(item.action)}
                    >
                      <span>{item.label}</span>
                      {item.shortcut && (
                        <span className="menu-shortcut">{item.shortcut}</span>
                      )}
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {guideDialogOpen && (
        <GuideWidthDialog
          currentWidth={optionsActions.guideWidth}
          onConfirm={(value) => {
            optionsActions.onSetGuideWidth(value);
            setGuideDialogOpen(false);
          }}
          onCancel={() => setGuideDialogOpen(false)}
        />
      )}

      {marginDialogOpen && (
        <MarginWidthDialog
          currentWidth={optionsActions.marginWidth}
          onConfirm={(value) => {
            optionsActions.onSetMarginWidth(value);
            setMarginDialogOpen(false);
          }}
          onCancel={() => setMarginDialogOpen(false)}
        />
      )}

      {lineStylesDialogOpen && (
        <LineStylesDialog
          currentStyles={optionsActions.lineStyles}
          multilingual={!!optionsActions.multilingual}
          onConfirm={(value) => {
            optionsActions.onSetLineStyles(value);
            setLineStylesDialogOpen(false);
          }}
          onCancel={() => setLineStylesDialogOpen(false)}
        />
      )}

      {aboutDialogOpen && (
        <AboutDialog onClose={() => setAboutDialogOpen(false)} />
      )}

      {tourOpen && (
        <WelcomeTour
          isAudible={isAudible}
          guideWidth={optionsActions.guideWidth}
          onClose={() => {
            setTourOpen(false);
            editor.commands.focus();
          }}
        />
      )}
    </div>
  );
};

export default Toolbar;
